import { type CSSProperties, useCallback, useMemo, useState } from 'react';

import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import { PANEL_WIDTH } from '@cartwhl/ui/layout/Panel';

import type { AppletLayoutProps } from '../../applets';
import CanvasLayoutDesktopPanel from './CanvasLayoutDesktopPanel';
import CanvasLayoutHeader from './CanvasLayoutHeader';
import CanvasLayoutMobilePanel from './CanvasLayoutMobilePanel';
import {
  CanvasLayoutContext,
  type CanvasLayoutHeaderContentSlot,
  type CanvasLayoutRegisteredSidePanel,
} from './canvasLayoutContext';

const CANVAS_LAYOUT_DEFAULT_MAIN_PADDING_MOBILE_PX = 16;
const CANVAS_LAYOUT_DEFAULT_MAIN_PADDING_DESKTOP_PX = 24;
const CANVAS_LAYOUT_DEFAULT_MAIN_PADDING_WITH_DESKTOP_PANEL_END_PX = 0;

export default function CanvasLayout({
  children,
  title,
  subtitle,
}: AppletLayoutProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [sidePanel, setSidePanel] =
    useState<CanvasLayoutRegisteredSidePanel | null>(null);
  const [sidePanelContentContainer, setSidePanelContentContainer] =
    useState<HTMLDivElement | null>(null);
  const [startHeaderContentContainer, setStartHeaderContentContainer] =
    useState<HTMLDivElement | null>(null);
  const [centerHeaderContentContainer, setCenterHeaderContentContainer] =
    useState<HTMLDivElement | null>(null);
  const [endHeaderContentContainer, setEndHeaderContentContainer] =
    useState<HTMLDivElement | null>(null);
  const [centerHeaderContentId, setCenterHeaderContentId] = useState<
    string | null
  >(null);
  const [endHeaderContentId, setEndHeaderContentId] = useState<string | null>(
    null,
  );

  const registerSidePanel = useCallback(
    (nextSidePanel: CanvasLayoutRegisteredSidePanel) => {
      setSidePanel(nextSidePanel);
    },
    [],
  );

  const clearSidePanel = useCallback((id: string) => {
    setSidePanel((currentSidePanel) => {
      if (currentSidePanel?.id !== id) {
        return currentSidePanel;
      }

      return null;
    });
  }, []);

  const registerHeaderContent = useCallback(
    (id: string, slot: CanvasLayoutHeaderContentSlot) => {
      if (slot === 'start') {
        return;
      }

      if (slot === 'center') {
        setCenterHeaderContentId(id);
        return;
      }

      setEndHeaderContentId(id);
    },
    [],
  );

  const clearHeaderContent = useCallback(
    (id: string, slot: CanvasLayoutHeaderContentSlot) => {
      if (slot === 'start') {
        return;
      }

      if (slot === 'center') {
        setCenterHeaderContentId((currentId) => {
          if (currentId !== id) {
            return currentId;
          }

          return null;
        });

        return;
      }

      setEndHeaderContentId((currentId) => {
        if (currentId !== id) {
          return currentId;
        }

        return null;
      });
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      registerSidePanel,
      clearSidePanel,
      sidePanelContentContainer,
      startHeaderContentContainer,
      centerHeaderContentContainer,
      endHeaderContentContainer,
      registerHeaderContent,
      clearHeaderContent,
    }),
    [
      centerHeaderContentContainer,
      clearHeaderContent,
      clearSidePanel,
      endHeaderContentContainer,
      registerHeaderContent,
      registerSidePanel,
      sidePanelContentContainer,
      startHeaderContentContainer,
    ],
  );

  const hasDesktopSidePanel = isDesktop && sidePanel !== null;
  const mainPaddingStartPx = isDesktop
    ? CANVAS_LAYOUT_DEFAULT_MAIN_PADDING_DESKTOP_PX
    : CANVAS_LAYOUT_DEFAULT_MAIN_PADDING_MOBILE_PX;
  const mainPaddingEndPx = hasDesktopSidePanel
    ? CANVAS_LAYOUT_DEFAULT_MAIN_PADDING_WITH_DESKTOP_PANEL_END_PX
    : mainPaddingStartPx;
  const mainClassName = `flex min-h-0 flex-1 flex-col transition-[padding] duration-200 px-4 ${
    hasDesktopSidePanel ? 'lg:pl-6 lg:pr-0' : 'lg:px-6'
  }`;

  return (
    <CanvasLayoutContext.Provider value={contextValue}>
      <div className="bg-primary flex h-screen w-full overflow-hidden">
        <div
          className="flex h-full min-w-0 flex-col transition-[width] duration-200"
          style={{
            width: hasDesktopSidePanel
              ? `calc(100% - ${PANEL_WIDTH}px)`
              : '100%',
          }}
        >
          <CanvasLayoutHeader
            title={title}
            subtitle={subtitle}
            hasCenterHeaderContent={centerHeaderContentId !== null}
            hasHeaderContent={
              centerHeaderContentId !== null || endHeaderContentId !== null
            }
            onStartHeaderContentContainerChange={setStartHeaderContentContainer}
            onCenterHeaderContentContainerChange={setCenterHeaderContentContainer}
            onEndHeaderContentContainerChange={setEndHeaderContentContainer}
          />
          <main
            className={mainClassName}
            style={
              {
                '--canvas-main-px-start': `${mainPaddingStartPx}px`,
                '--canvas-main-px-end': `${mainPaddingEndPx}px`,
              } as CSSProperties
            }
          >
            <div className="relative z-0 flex min-h-0 flex-1 flex-col">
              {children}
            </div>
          </main>
        </div>
        <CanvasLayoutDesktopPanel
          sidePanel={sidePanel}
          isVisible={hasDesktopSidePanel}
          onContentContainerChange={setSidePanelContentContainer}
        />
        {!isDesktop && (
          <CanvasLayoutMobilePanel
            sidePanel={sidePanel}
            onContentContainerChange={setSidePanelContentContainer}
          />
        )}
      </div>
    </CanvasLayoutContext.Provider>
  );
}
